// AI Review Workspace — presentation-only registry + pure helpers.
// Review-specific: co-located with the page that owns it. No AI calls, no side effects.

export const COMPLEXITY_TONE = {
  Simple: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Moderate: "bg-blue-50 text-blue-700 border-blue-200",
  Complex: "bg-amber-50 text-amber-700 border-amber-200",
  "Very Complex": "bg-rose-50 text-rose-700 border-rose-200",
};

export const SECTION_META = {
  record_updates: { group: "Case Review", label: "Client Record Updates", icon: "UserCog" },
  summary: { group: "Case Review", label: "Internal Case Summary", icon: "ClipboardList" },
  services: { group: "Case Review", label: "Services & Pricing", icon: "ListChecks" },
  tax_situation: { group: "Case Review", label: "Tax Situation", icon: "Flag" },
  risks: { group: "Case Review", label: "Risk Flags", icon: "AlertTriangle" },
  missing_docs: { group: "Case Review", label: "Missing Documents", icon: "FileSearch" },
  estimate: { group: "Client Documents", label: "Estimate", icon: "FileText" },
  email: { group: "Client Documents", label: "Summary Email", icon: "Mail" },
  engagement: { group: "Client Documents", label: "Engagement Letter", icon: "FileSignature" },
  approval: { group: "Final", label: "Approval & Send", icon: "CheckCircle2" },
};

export const GROUP_ORDER = ["Case Review", "Client Documents", "Final"];

export const STATUS_META = {
  not_reviewed: { label: "Not Reviewed", icon: "Circle", cls: "text-slate-300", pill: "bg-slate-100 text-slate-500 border-slate-200" },
  in_review: { label: "In Review", icon: "CircleDot", cls: "text-blue-500", pill: "bg-blue-50 text-blue-700 border-blue-200" },
  reviewed: { label: "Reviewed", icon: "CheckCircle2", cls: "text-emerald-500", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  needs_attention: { label: "Needs Attention", icon: "AlertTriangle", cls: "text-amber-500", pill: "bg-amber-50 text-amber-700 border-amber-200" },
};

export function sectionKeys(data) {
  const ru = data?.record_updates || {};
  const hasRU = (ru.auto_applied || []).length > 0 || (ru.proposed || []).length > 0;
  return [
    ...(hasRU ? ["record_updates"] : []),
    "summary", "tax_situation", "risks", "missing_docs", "services",
    "estimate", "email", "engagement", "approval",
  ];
}

export const fmtVal = (v) =>
  v === true ? "Yes" : v === false ? "No" : (v === null || v === "" || v === undefined) ? "—" : String(v);

// Deterministic overall recommendation, derived from the existing analysis.
export function buildRecommendation({ isRejected, lowConfidence, attentionCount, pendingCount, riskCount, missingCount, confidence, firstUnfinished }) {
  let recommendation, recTone, recIcon;
  if (isRejected) { recommendation = "Reject & Re-run Analysis"; recTone = "rose"; recIcon = "XCircle"; }
  else if (lowConfidence || attentionCount > 0 || pendingCount > 0) { recommendation = "Review Before Approving"; recTone = "amber"; recIcon = "AlertTriangle"; }
  else if (riskCount > 0 || missingCount > 0) { recommendation = "Approve After Minor Edits"; recTone = "blue"; recIcon = "PencilLine"; }
  else { recommendation = "Approve"; recTone = "emerald"; recIcon = "CheckCircle2"; }
  const recToneCls = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  }[recTone];

  const why = [];
  if (isRejected) why.push("This analysis was rejected — re-run with corrections before it can advance.");
  if (lowConfidence) why.push(`Confidence is ${confidence}% — below the 85% approval threshold.`);
  else why.push(`Confidence is ${confidence}% — meets the approval threshold.`);
  if (riskCount) why.push(`${riskCount} risk flag${riskCount > 1 ? "s" : ""} to review before approval.`);
  if (missingCount) why.push(`${missingCount} missing document${missingCount > 1 ? "s" : ""} will become client requests on approval.`);
  if (pendingCount) why.push(`${pendingCount} proposed client record update${pendingCount > 1 ? "s" : ""} awaiting your decision.`);
  if (why.length < 2) why.push("Pricing and recommended services are ready for review.");
  const whyBullets = why.slice(0, 4);

  let nextAction;
  if (isRejected) nextAction = "Re-run Analysis";
  else if (firstUnfinished) nextAction = `Review ${SECTION_META[firstUnfinished].label}`;
  else nextAction = "Ready for Approval";

  // Plain-language explanation for the consolidated Review Summary card.
  let explanation;
  if (isRejected) explanation = "Allison's analysis was rejected — it won't advance until it's re-run with your corrections.";
  else if (lowConfidence) explanation = `Allison is ${confidence}% confident on this estimate, which is below the 85% threshold. Review each section carefully before approving.`;
  else if (attentionCount > 0 || pendingCount > 0) explanation = "A few items need your attention before this estimate is ready to send. Work through the key items below, then approve.";
  else if (riskCount > 0 || missingCount > 0) explanation = "This estimate looks solid. A couple of minor edits and it's ready to send to the client.";
  else explanation = "Everything checks out. Review the sections and approve to send the estimate.";

  return { recommendation, recToneCls, recIcon, whyBullets, nextAction, explanation };
}
